import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { adminFetch, AdminAuthError } from './adminAuth';

/**
 * Error message from an API response body, falling back to the status line
 */
export async function responseError(response) {
  try {
    const body = await response.json();
    if (body && body.error) {
      return body.error;
    }
  } catch (err) {
    // Not JSON; fall through
  }
  return `Request failed (${response.status})`;
}

// A paused database (dev's Aurora cluster sleeps when idle) takes up to ~30 seconds to wake
const WAKE_RETRY_DELAY_MS = 5000;
const WAKE_RETRY_ATTEMPTS = 6;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Loads JSON from an admin API path. Returns { data, error, loading, waking, reload }.
 * A 503 (database waking up) is retried with waking set; an ended session sends the
 * admin back to sign in, then returns them here.
 */
export default function useAdminData(path, { retryDelayMs = WAKE_RETRY_DELAY_MS } = {}) {
  const history = useHistory();
  const location = useLocation();
  const [state, setState] = useState({ data: null, error: null, loading: true, waking: false });

  const load = useCallback(async () => {
    setState(current => ({ ...current, error: null, loading: true, waking: false }));
    try {
      let response = await adminFetch(path);
      for (let attempt = 1; response.status === 503 && attempt < WAKE_RETRY_ATTEMPTS; attempt++) {
        setState(current => ({ ...current, waking: true }));
        await wait(retryDelayMs);
        response = await adminFetch(path);
      }
      if (!response.ok) {
        throw new Error(await responseError(response));
      }
      setState({ data: await response.json(), error: null, loading: false, waking: false });
    } catch (err) {
      if (err instanceof AdminAuthError) {
        history.replace({ pathname: '/admin/login', state: { from: location } });
        return;
      }
      setState({ data: null, error: err.message, loading: false, waking: false });
    }
  // location is only used for the return trip after signing in
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, history, retryDelayMs]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
}
